// WHAT: React Context for managing navigation path state and breadcrumbs
// WHY:  Provides breadcrumb navigation and child route information based on current path
// DOMAIN: Shared → Contexts
// DEPENDENCIES: react-router-dom, navigation maps from app/navigation
// RISKS:
//   - NOTE: Depends on navigation maps being correctly initialized
//   - NOTE: Breadcrumb calculation may fail if navigation structure is invalid
//   - NOTE: Uses useMemo but depends on external maps - may not update if maps change

import { useMemo, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

// eslint-disable-next-line boundaries/element-types -- Reason: Navigation maps are infrastructure utilities, not domain-specific features. This is a shared utility dependency.
import { linkMap, nodeMap, parentMap, type NavNode } from "@/app/navigation";

import { PathContext, type PathState } from "./PathContext.shared";

/**
 * WHAT:
 *   Provider component that calculates and provides breadcrumb navigation and
 *   child route information based on the current pathname.
 *
 * WHY:
 *   Provides a centralized way to access navigation context (breadcrumbs, children)
 *   without manually traversing the navigation tree in each component.
 *
 * BEHAVIOR:
 *   - Calculates breadcrumb chain by traversing parent nodes
 *   - Finds current node from linkMap
 *   - Provides children nodes if available
 *   - Memoizes calculation based on pathname
 *
 * RISKS:
 *   - NOTE: Depends on navigation maps being correctly initialized
 *   - NOTE: Breadcrumb calculation may fail if navigation structure is invalid
 *   - NOTE: Uses useMemo but depends on external maps - may not update if maps change
 *   - NOTE: Excludes root node from breadcrumb - may cause issues if root is needed
 *
 * @param root0 - Props object
 *
 * @param root0.children - Child components
 *
 * @returns PathProvider component
 *
 */
export const PathProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();

  const value = useMemo(() => {
    const leaf = linkMap.get(location.pathname);
    const chain: NavNode[] = [];
    let node = leaf;
    while (node) {
      if (node.id !== "root") {
        chain.push(node);
      }
      const pid = parentMap.get(node.id);
      node = pid ? nodeMap.get(pid) : undefined;
    }
    // Create reversed copy to avoid mutating original array
    const reversedChain: NavNode[] = [];
    for (let i = chain.length - 1; i >= 0; i--) {
      const node = chain[i];
      if (node) {
        reversedChain.push(node);
      }
    }
    const state: PathState = {
      breadcrumb: reversedChain,
      pathname: location.pathname,
    };
    if (leaf?.children !== undefined) {
      state.children = leaf.children;
    }
    return state;
  }, [location.pathname]);

  return <PathContext.Provider value={value}>{children}</PathContext.Provider>;
};
